<template>
    <div
        class="tab"
        :id="options.id"
        v-show="show"
        :class="[{ 'only-one': panels.length === 1, fill: options.fill }, state, { 'no-border': !bordered }]"
        :style="styleFormatter"
        v-observe-size="initTabBtn"
        ref="tab"
    >
        <div v-if="options.shrink" class="tab-switch-icon" v-on:click="toggle">
            <shrink-img class="tab-switch-icon" v-bind="{ color: '#999', width: '16px', height: '16px' }" />
        </div>
        <div class="tab-header" :class="{ switch: options.shrink }" ref="tab-header">
            <div
                class="flip left"
                :class="{ use: btnState.left }"
                @click="scrollTab('previous')"
                v-show="btnState.isOperate"
            >
                <tabFlipImg :color="btnState.left ? '#999' : '#d9d9d9'" />
            </div>
            <div class="content-wrapper" ref="content-wrapper">
                <ul class="content" ref="content" :class="{ move: btnState.isOperate }">
                    <li
                        v-for="(item, index) in panels"
                        :class="{ active: showIndex === index }"
                        @click="switchPanel(index)"
                        :key="item.id || item.title || index"
                        v-show="item.show !== false"
                        :id="item.id"
                    >
                        <span> {{ propI18N(item.title) }}</span>
                        <span class="number" v-show="panelsNums[index]">{{ panelsNums[index] }}</span>
                        <PanelWrapper v-if="pageEditing" :options="item" @click="() => switchPanel(index)" />
                    </li>
                </ul>
                <div class="tab-bar" :style="tabBarStyleObject" v-if="panels.length !== 1"></div>
            </div>
            <div
                class="flip right"
                :class="{ use: btnState.right }"
                @click="scrollTab('next')"
                v-show="btnState.isOperate"
            >
                <tabFlipImg :color="btnState.right ? '#999' : '#d9d9d9'" />
            </div>
        </div>
        <transition
            @before-enter="beforeEnter"
            @enter="enter"
            @after-enter="afterEnter"
            @before-leave="beforeLeave"
            @leave="leave"
            @after-leave="afterLeave"
        >
            <div class="tab-content" v-show="state !== 'collapsed'" :class="{ 'auto-height': height === 'auto' }">
                <div class="tab-content-wrapper" ref="paneWrapper" :style="panelWrapperStyle">
                    <div
                        v-for="(item, index) in panels"
                        class="tab-pane"
                        :class="{ active: showIndex === index }"
                        :key="item.id || item.title || index"
                        v-show="item.show !== false"
                        :id="item.id"
                    >
                        <vuescroll :ops="scrollOptions">
                            <div class="panel-body" ref="panelBody" :class="heightAuto">
                                <slot :name="item.id"></slot>
                            </div>
                        </vuescroll>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import Gikam from 'gikam';
import vuescroll from 'vuescroll';
import PanelWrapper from './tab-panel-page-edit-wrapper';

export default {
    components: {
        vuescroll,
        PanelWrapper
    },

    props: {
        options: Object
    },

    data() {
        return {
            panels: this.options.panels,
            showIndex: -1,
            state: Gikam.isEmpty(this.options.expand) || this.options.expand ? 'expand' : 'collapsed',
            show: this.options.show,
            //标记当前展示页签
            tabIndex: 0,
            tabSizeTimeout: null,
            panelsNums: this.options.panelsShowNum || new Array(this.options.panels.length),
            btnState: {
                //页签向左按钮是否可用
                left: false,
                //页签向右按钮是否可用
                right: false,
                //标记页签是否出现滚动条
                isOperate: null
            },
            // 是否显示边框
            bordered: this.options.bordered,
            styleFormatter: {},
            scrollOptions: {
                bar: {
                    background: 'rgb(187, 187, 187)',
                    opacity: 0.8,
                    size: '8px',
                    onlyShowBarOnScroll: false,
                    keepShow: true
                },
                scrollPanel: {
                    scrollingX: false
                }
            },
            tabBarStyleObject: {
                width: 0,
                transform: null
            },
            pageEditing: Gikam.getCurrentWindow()?.pageEditing
        };
    },

    computed: {
        height() {
            if (this.options.fill) {
                return '100%';
            }
            const height = this.options.height;
            if (!height) {
                return;
            }
            return Gikam.isNumber(height) ? height + 'px' : height;
        },

        heightAuto() {
            return this.options.height == 'auto' && this.options.fill === false ? 'height-auto' : '';
        },

        panelWrapperStyle() {
            const panelWrapperStyle = {};
            if (this.options.panelSwitchAnimate === false) {
                panelWrapperStyle.transition = 'none';
            }
            return panelWrapperStyle;
        }
    },

    mounted() {
        this.showIndex = this.options.showIndex;
        this.initTabBtn();
        this.notify();
        this.initPanelPosition();
        this.initStyle();
    },

    watch: {
        showIndex(index) {
            this.tab.trigger('actived', {
                index: index,
                title: this.panels[index] && Gikam.propI18N(this.panels[index].title)
            });
            this.activateTab();
        },

        'options.panels'(val) {
            this.panels = val;
        }
    },

    inject: ['tab'],

    methods: {
        setPanelBadge(id, number) {
            const { index } = this.getPanelDataById(id);
            if (index === undefined) {
                return;
            }
            this.$set(this.panelsNums, index, number);
        },

        //点击页签左右按钮，移动页签位置
        scrollTab(directionStr) {
            //用于计算移动距离
            let direction = 1;

            //点击向左移动页签
            if (directionStr === 'previous') {
                if (!this.btnState.left) return;
                //移动当前展示页签标记位
                this.tabIndex--;
                direction = -1;
                //向右移动标签页
            } else if (directionStr === 'next') {
                if (!this.btnState.right) return;
                this.tabIndex++;
            }

            const wrapperWidth = this.$refs['content-wrapper'].offsetWidth;
            const scrollWidth = this.$refs['content-wrapper'].scrollWidth;

            //获取当前展示页签平均宽度，用于移动滚动条
            const liArray = scrollWidth / this.panels.length;

            //设置滚动条位置
            const scrollLeft = (this.$refs['content-wrapper'].scrollLeft += liArray * direction);

            //改变按钮状态
            this.btnState.left = true;
            this.btnState.right = true;
            //判断按钮极限值
            if (this.tabIndex === 0) this.btnState.left = false;
            else if (scrollLeft >= scrollWidth - wrapperWidth) this.btnState.right = false;
        },

        initTabBtn() {
            //获取页签容器宽度
            let wrapperWidth = this.$refs['content-wrapper'].offsetWidth;
            //获取页签真实宽度
            let scrollWidth = this.$refs['content-wrapper'].scrollWidth;
            if (wrapperWidth === 0 || scrollWidth === 0) return;
            if (this.tabIndex !== 0) this.btnState.left = true;
            //判断右侧页签是否处于可点击状态
            if (scrollWidth > wrapperWidth) {
                this.btnState.right = true;
                this.btnState.isOperate = true;
                //判断页签左右移动按钮是否可点击
            } else {
                this.btnState.isOperate = false;
            }
        },

        switchPanel(index) {
            const { onBeforePanelSwitch } = this.options;
            if (onBeforePanelSwitch?.call(this.tab, this.panels[index]) === false) {
                return;
            }
            this.showIndex = index;
        },

        switchTabIndex(index) {
            this.tabIndex = index;
        },

        //开关tab
        toggle() {
            if (!this.state || this.state === 'expand') {
                this.state = 'collapsed';
            } else {
                this.state = 'expand';
            }
            this.options.onToggle?.call(this.tab, this.state === 'expand');
        },

        propI18N(text) {
            return Gikam.propI18N(text);
        },

        notify() {
            this.$nextTick(() => {
                if (!this.options.id) {
                    return;
                }
                const pro = Gikam.createdComponent[this.options.id];
                if (pro) {
                    pro.resolve(this.tab);
                    delete Gikam.createdComponent[this.options.id];
                }
            });
        },

        //获取指定id的panel的数据和索引
        getPanelDataById(id) {
            const index = this.panels.findIndex(item => item.id === id);
            return { index, data: this.panels[index] };
        },

        //显示相应panel
        showPanel(id) {
            const panel = this.getPanelDataById(id);
            if (Gikam.isNotEmpty(panel.index) && panel.data.show !== false) {
                this.showIndex = panel.index;
            } else {
                Gikam.error(`can not find ${id} when use showPanel`);
            }
        },

        //移除tab下的面板
        removePanel(id) {
            // 当加载完成后再执行
            this.$nextTick(async () => {
                const panel = this.getPanelDataById(id);
                if (Gikam.isNotEmpty(panel.data)) {
                    this.$set(panel.data, 'show', false);
                } else {
                    Gikam.error(`can not find ${id} when use removePanel`);
                }
                await this.$nextTick();
                //如果隐藏当前展示的panel，则隐藏之后需要前面或后面的panle进行展示
                if (panel.index === this.showIndex) {
                    this.showIndex = this.panels.findIndex(item => item.show !== false);
                } else {
                    panel.index < this.showIndex && this.activateTab();
                }
            });
        },

        //显示tab下的面板
        async revertPanel(id) {
            const panel = this.getPanelDataById(id);
            Gikam.isNotEmpty(panel) && this.$set(panel.data, 'show', true);
            await this.activateTab();
        },

        async activateTab() {
            await this.$nextTick();
            const headerUl = this.$refs.content;
            if (!headerUl) {
                return;
            }
            const activeItem = headerUl.childNodes[this.showIndex];
            this.tabBarStyleObject = {
                width: `${activeItem.offsetWidth}px`,
                transform: `translateX(${activeItem.offsetLeft}px)`
            };
            this.setActivePane();
        },

        initPanelPosition() {
            this.options.panels.forEach((panel, index) => {
                const dom = this.$refs['panelBody'][index];
                if (Gikam.isNotEmpty(panel.items)) {
                    if (
                        (panel.items.length === 1 && panel.items[0].type.toLowerCase() === 'form') ||
                        panel.items.length > 1
                    ) {
                        dom.style.position = 'relative';
                    }
                }
            });
        },

        //规避expand:false 高度不为auto场景
        initStyle() {
            const height = this.state === 'collapsed' ? 'auto' : this.height;
            this.styleFormatter = Gikam.deepExtend(this.options.style, { height: height });
        },

        getPanelHeight() {
            if (this.height === '100%') {
                this.$refs['tab'].style.height = '100%';
                const tabHeight = this.$refs['tab'].scrollHeight;
                this.$refs['tab'].style.height = 'auto';
                return tabHeight - this.$refs['tab-header'].scrollHeight - 18 + 'px';
            } else if (this.height === 'auto') {
                const height = this.$refs['panelBody'][this.showIndex].scrollHeight + 5;
                return height + 'px';
            } else {
                return parseFloat(this.height) - this.$refs['tab-header'].scrollHeight + 'px';
            }
        },

        //将panel元素高度设置为0，可以读取scrollHeight;
        beforeEnter(el) {
            this.removeClass(el, 'fade-in');
            this.addClass(el, 'tab-transition fade-in');
            if (!el.dataset) {
                el.dataset = {};
            }
            el.dataset.oldTabHeight = this.height || this.$refs['tab'].scrollHeight;
            el.style.height = '0';
        },

        enter(el) {
            el.dataset.oldOverflow = el.style.overflow;
            el.style.height = this.getPanelHeight();
            this.removeClass(el, 'fade-in');
            this.addClass(el, 'fade-out');
            el.style.overflow = 'hidden';
        },

        afterEnter(el) {
            this.removeClass(el, 'tab-transition');
            el.style.height = '';
            el.style.overflow = el.dataset.oldOverflow;
            this.$refs['tab'].style.height = el.dataset.oldTabHeight;
        },

        beforeLeave(el) {
            if (!el.dataset) {
                el.dataset = {};
            }
            el.dataset.oldOverflow = el.style.overflow;
            this.addClass(el, 'fade-out');
            el.style.height = this.getPanelHeight();
            el.style.overflow = 'hidden';
            this.$refs['tab'].style.height = '';
        },

        leave(el) {
            if (el.scrollHeight !== 0) {
                this.removeClass(el, 'fade-out');
                this.addClass(el, 'tab-transition fade-in');
                el.style.height = 0;
            }
        },

        afterLeave(el) {
            this.removeClass(el, 'tab-transition');
            el.style.height = '';
            el.style.overflow = el.dataset.oldOverflow;
        },

        addClass(el, cls) {
            const curClass = el.className;
            if (curClass.indexOf(cls) < 0) {
                el.className = curClass + ' ' + cls;
            }
        },

        removeClass(el, cls) {
            const curClass = el.className;
            el.className = curClass.replace(cls, '');
        },

        setActivePane() {
            const paneWrapper = this.$refs.paneWrapper;
            if (!paneWrapper) {
                return;
            }
            const activePanel = this.panels[this.showIndex];
            const shownPanels = this.panels.filter(panel => panel.show !== false);
            paneWrapper.style.marginLeft = `-${100 * shownPanels.indexOf(activePanel)}%`;
        }
    }
};
</script>

<style scoped lang="scss">
@import '../css/tab.scss';
</style>
